import React from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, FolderPlus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';

/**
 * Admin › Category Suggestions.
 *
 * AI-proposed new ConnectWise type/subtype/item values per board (seeded from
 * category-gap analysis of uncategorized tickets). "Add to ConnectWise"
 * creates the values in ConnectWise via the API's safe catalog write path
 * (find-or-create → re-fetch confirm → audit → mark added). Duplicates are
 * reported honestly ("already exists"), never re-created. When the
 * ConnectWise integration is disconnected the Add action is shown as
 * unavailable instead of pretending it could work.
 */

type SuggestionStatus = 'proposed' | 'added' | 'rejected';
type ApproveOutcome = 'created' | 'reactivated' | 'already_exists';

interface CategorySuggestion {
  id: string;
  cwBoardId: number;
  boardName: string;
  suggestedType: string;
  suggestedSubtype: string | null;
  suggestedItem: string | null;
  frequency: number;
  exampleTitles: Array<string>;
  status: SuggestionStatus;
  source: string | null;
  cwTypeId: number | null;
  cwSubtypeId: number | null;
  cwItemId: number | null;
  addedBy: string | null;
  addedAt: string | null;
}

interface BoardGroup {
  cwBoardId: number;
  boardName: string;
  items: Array<CategorySuggestion>;
}

interface SuggestionsResponse {
  boards: Array<BoardGroup>;
  cwConnected: boolean;
}

interface CatalogEntryResolution {
  id: number;
  name: string;
  action: 'created' | 'reused' | 'reactivated';
}

interface ApproveResponse {
  ok: true;
  outcome: ApproveOutcome;
  type: CatalogEntryResolution;
  subtype: CatalogEntryResolution | null;
  item: CatalogEntryResolution | null;
}

type RowPhase =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string };

function describeEntry(label: string, entry: CatalogEntryResolution): string {
  if (entry.action === 'created') return label + ' “' + entry.name + '” created (CW id ' + entry.id + ')';
  if (entry.action === 'reactivated') return label + ' “' + entry.name + '” reactivated (CW id ' + entry.id + ')';
  return label + ' “' + entry.name + '” already existed (CW id ' + entry.id + ')';
}

function StatusBadge({ status }: { status: SuggestionStatus }) {
  if (status === 'added') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
        <Check className="h-3 w-3" /> Added
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-200 dark:bg-zinc-500/20 px-2 py-0.5 text-[11px] font-semibold text-zinc-600 dark:text-zinc-300">
        <X className="h-3 w-3" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 dark:bg-brand-500/15 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:text-brand-300">
      Proposed
    </span>
  );
}

function categoryPath(s: CategorySuggestion): string {
  return [s.suggestedType, s.suggestedSubtype, s.suggestedItem].filter(Boolean).join(' → ');
}

export function CategorySuggestionsPage() {
  const { data, loading, error, refetch } = useGet<SuggestionsResponse>('/api/v1/admin/category-suggestions');
  const [phases, setPhases] = React.useState<Record<string, RowPhase>>({});
  const [expandedExamples, setExpandedExamples] = React.useState<Record<string, boolean>>({});
  const [collapsedBoards, setCollapsedBoards] = React.useState<Record<number, boolean>>({});
  const [confirmRejectId, setConfirmRejectId] = React.useState<string | null>(null);
  const [showDecided, setShowDecided] = React.useState(false);

  const boards = data?.boards ?? [];
  const cwConnected = data?.cwConnected ?? false;
  const decidedCount = boards.flatMap(b => b.items).filter(i => i.status !== 'proposed').length;

  async function approve(s: CategorySuggestion) {
    setPhases(prev => ({ ...prev, [s.id]: { kind: 'busy' } }));
    try {
      const r = await apiPost<ApproveResponse>('/api/v1/admin/category-suggestions/' + s.id + '/approve', {});
      const parts = [
        describeEntry('Type', r.type),
        r.subtype ? describeEntry('Subtype', r.subtype) : null,
        r.item ? describeEntry('Item', r.item) : null,
      ].filter(Boolean).join('; ');
      const message = r.outcome === 'already_exists'
        ? 'Already in ConnectWise — nothing was created. ' + parts
        : parts;
      setPhases(prev => ({ ...prev, [s.id]: { kind: 'done', message } }));
      toast.success(
        r.outcome === 'already_exists'
          ? 'Already existed in ConnectWise — marked added'
          : 'Created in ConnectWise: ' + categoryPath(s),
      );
      refetch();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'request failed';
      setPhases(prev => ({ ...prev, [s.id]: { kind: 'error', message } }));
      toast.error('Could not add ' + categoryPath(s) + ': ' + message);
    }
  }

  async function reject(s: CategorySuggestion) {
    setPhases(prev => ({ ...prev, [s.id]: { kind: 'busy' } }));
    setConfirmRejectId(null);
    try {
      await apiPost('/api/v1/admin/category-suggestions/' + s.id + '/reject', {});
      setPhases(prev => ({ ...prev, [s.id]: { kind: 'done', message: 'Rejected.' } }));
      toast.success('Rejected ' + categoryPath(s));
      refetch();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'request failed';
      setPhases(prev => ({ ...prev, [s.id]: { kind: 'error', message } }));
      toast.error('Could not reject: ' + message);
    }
  }

  return (
    <div className="p-6 space-y-6" role="region" aria-label="Category suggestions">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Category Suggestions</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
          AI-proposed new ConnectWise types, subtypes and items per board, from analysis of uncategorized
          tickets. Adding one creates it in ConnectWise immediately (existing values are reused, never
          duplicated).
        </p>
      </div>

      {!loading && data && !cwConnected && (
        <div
          className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4"
          data-testid="cw-disconnected-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-semibold">ConnectWise is not connected.</span> “Add to ConnectWise” is
            unavailable until the integration is connected under Admin › Integrations. Rejecting suggestions
            still works.
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} lines={4} />)}
        </div>
      ) : error ? (
        <ErrorState message={'Failed to load category suggestions (' + error + ').'} onRetry={refetch} />
      ) : boards.length === 0 ? (
        <EmptyState
          icon="🗂️"
          title="No category suggestions yet"
          description="New proposals appear here after a category-gap analysis run seeds them (pnpm --filter @hts/db db:seed:category-suggestions)."
        />
      ) : (
        <>
          {decidedCount > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowDecided(v => !v)}
                className="text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] underline underline-offset-2"
              >
                {showDecided ? 'Hide decided (' + decidedCount + ')' : 'Show decided (' + decidedCount + ')'}
              </button>
            </div>
          )}
          <div className="space-y-4">
            {boards.map(board => {
              const visible = board.items.filter(i => showDecided || i.status === 'proposed');
              if (visible.length === 0) return null;
              const proposed = board.items.filter(i => i.status === 'proposed').length;
              const collapsed = collapsedBoards[board.cwBoardId] ?? false;
              const totalFreq = board.items.filter(i => i.status === 'proposed').reduce((sum, i) => sum + i.frequency, 0);
              return (
                <div
                  key={board.cwBoardId}
                  data-testid={'board-' + board.cwBoardId}
                  className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setCollapsedBoards(prev => ({ ...prev, [board.cwBoardId]: !collapsed }))}
                    aria-expanded={!collapsed}
                    className="w-full px-5 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] flex items-center gap-3 text-left"
                  >
                    {collapsed
                      ? <ChevronRight className="h-4 w-4 text-[hsl(var(--content-subtle))]" />
                      : <ChevronDown className="h-4 w-4 text-[hsl(var(--content-subtle))]" />}
                    <h3 className="text-base font-semibold text-[hsl(var(--content))]">{board.boardName}</h3>
                    <span className="text-xs text-[hsl(var(--content-subtle))]">
                      {proposed} proposed · covers {totalFreq.toLocaleString()} tickets/yr
                    </span>
                  </button>
                  {!collapsed && (
                    <ul className="divide-y divide-[hsl(var(--border))]">
                      {visible.map(s => {
                        const phase = phases[s.id] ?? { kind: 'idle' as const };
                        const busy = phase.kind === 'busy';
                        const examplesOpen = expandedExamples[s.id] ?? false;
                        return (
                          <li key={s.id} data-testid={'suggestion-' + s.id} className="px-5 py-4 space-y-2">
                            <div className="flex items-start gap-3 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-[hsl(var(--content))]">{categoryPath(s)}</span>
                                  <StatusBadge status={s.status} />
                                </div>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="inline-flex items-center rounded-full bg-[hsl(var(--surface-subtle))] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--content-subtle))]">
                                    {s.frequency.toLocaleString()} tickets/yr
                                  </span>
                                  {s.exampleTitles.length > 0 ? (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedExamples(prev => ({ ...prev, [s.id]: !examplesOpen }))}
                                      aria-expanded={examplesOpen}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400 hover:underline underline-offset-2"
                                    >
                                      {examplesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                      {s.exampleTitles.length} example{s.exampleTitles.length === 1 ? '' : 's'}
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-[hsl(var(--content-placeholder))]">No example titles captured</span>
                                  )}
                                </div>
                              </div>
                              {s.status === 'proposed' && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {confirmRejectId === s.id ? (
                                    <>
                                      <span className="text-xs text-[hsl(var(--content-subtle))]">Reject this suggestion?</span>
                                      <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => reject(s)}
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                                      >
                                        Confirm reject
                                      </button>
                                      <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => setConfirmRejectId(null)}
                                        className="rounded-lg px-2 py-1.5 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        disabled={busy || !cwConnected}
                                        title={cwConnected ? 'Create in ConnectWise' : 'Unavailable: ConnectWise is not connected'}
                                        onClick={() => approve(s)}
                                        className={cn(
                                          'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white',
                                          cwConnected ? 'bg-brand-600 hover:bg-brand-700' : 'bg-zinc-400 dark:bg-zinc-600 cursor-not-allowed',
                                          'disabled:opacity-70',
                                        )}
                                      >
                                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
                                        {busy ? 'Adding…' : 'Add to ConnectWise'}
                                      </button>
                                      <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => setConfirmRejectId(s.id)}
                                        className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-red-600 hover:border-red-300 dark:hover:text-red-400"
                                      >
                                        Reject
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            {examplesOpen && s.exampleTitles.length > 0 && (
                              <ul className="rounded-lg bg-[hsl(var(--surface-subtle))] px-3 py-2 space-y-1">
                                {s.exampleTitles.map((title, i) => (
                                  <li key={i} className="text-xs text-[hsl(var(--content-subtle))] font-mono truncate" title={title}>
                                    “{title}”
                                  </li>
                                ))}
                              </ul>
                            )}
                            {phase.kind === 'done' && (
                              <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5" /> {phase.message}
                              </p>
                            )}
                            {phase.kind === 'error' && (
                              <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5 flex-wrap">
                                <AlertTriangle className="h-3.5 w-3.5" /> {phase.message}
                                <button
                                  type="button"
                                  onClick={() => approve(s)}
                                  className="underline underline-offset-2 font-medium"
                                >
                                  Retry
                                </button>
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
